/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {areSetsEqual} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {Collaborator, Owner, OwnerInfo} from 'auth/PermissionConstants';
import {convertCollaborationsToCollaborators} from 'auth/PermissionUtil';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isOrganization} from 'constants/UserConstants';
import {OwnableInfo, UpdateOwnableParams} from 'create-components/OwnableConstants';
import {OwnableSettingsWithCollaborationsModal} from 'create-components/OwnableSettingsModal';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import TemplateCollectionActions from 'notebook/entry-templates/collections/TemplateCollectionActions';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import Link from 'ui-library/Link';

import {TemplateCollectionGraphQL} from '../../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';
import {CreateTemplateParams} from './TemplateCollectionDAO';
import {OwnerScopedReviewProcessSection} from './TemplateCollectionReviewProcessSection';
import {persistReviewLifecycle} from './TemplateCollectionReviewProcessUtils';
import {doesTemplateCollectionIncludeProcedures, getNumTemplatesOfType} from './TemplateCollectionTypesUtils';

interface Props {
  templateCollection: TemplateCollectionGraphQL;
  navigateOnSuccess?: boolean;
  onRequestClose: (...args: Array<any>) => any;
  onFilterViewToProceduresOnly?: () => void;
}

interface State {
  reviewLifecycleIds: Set<string>;
}

class ManageTemplateCollectionModal extends React.Component<Props, State> {
  _isMounted?: boolean;

  private readonly initialReviewLifecycleIds: Set<string>;

  constructor(props: Props) {
    super(props);
    this.initialReviewLifecycleIds = new Set(
      props.templateCollection?.reviewLifecycles?.map(({id}) => id) ?? []
    );
    this.state = {reviewLifecycleIds: new Set(this.initialReviewLifecycleIds)};
  }

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  private reviewProcessSelectionEnabled(): boolean {
    return BenchlingConfig.ELN_ENABLE_TEMPLATE_REVIEWS;
  }

  private clearReviewLifecycleSelection = (): void => {
    this.setState({reviewLifecycleIds: new Set()});
  };

  // Invoked when selecting an option from the dropdown, which toggles the selection on or off.
  toggleReviewLifecycleId = (reviewLifecycleId: string): void => {
    this.setState((state) => {
      const reviewLifecycleIds = new Set(state.reviewLifecycleIds);
      if (reviewLifecycleIds.has(reviewLifecycleId)) {
        reviewLifecycleIds.delete(reviewLifecycleId);
      } else {
        reviewLifecycleIds.add(reviewLifecycleId);
      }
      return {reviewLifecycleIds};
    });
  };

  // Invoked when clicking the "x" on a selected chip in the input field, which only removes.
  removeReviewLifecycleId = (reviewLifecycleId: string): void => {
    this.setState((state) => {
      const reviewLifecycleIds = new Set(state.reviewLifecycleIds);
      reviewLifecycleIds.delete(reviewLifecycleId);
      return {reviewLifecycleIds};
    });
  };

  private renderReviewProcessSection = ({
    isPosting,
    owner,
  }: {
    isPosting?: boolean;
    owner: Owner;
  }): JSX.Element | null => {
    if (!this.reviewProcessSelectionEnabled()) {
      return null;
    }
    return (
      <OwnerScopedReviewProcessSection
        owner={owner}
        reviewLifecycleIds={this.state.reviewLifecycleIds}
        onOwnerScopeChange={this.clearReviewLifecycleSelection}
        toggleReviewLifecycleId={this.toggleReviewLifecycleId}
        removeReviewLifecycleId={this.removeReviewLifecycleId}
        isReadOnly={!this.props.templateCollection?.permissions?.admin || Boolean(isPosting)}
      />
    );
  };

  private saveReviewLifecycleIfChanged = (): Promise<void> => {
    if (
      !this.reviewProcessSelectionEnabled() ||
      areSetsEqual(this.state.reviewLifecycleIds, this.initialReviewLifecycleIds)
    ) {
      return Promise.resolve();
    }
    return persistReviewLifecycle(this.props.templateCollection.id, this.state.reviewLifecycleIds);
  };

  updateTemplateCollection = async (data: CreateTemplateParams): Promise<any> => {
    const response = await TemplateCollectionActions.updateWithXHR(this.props.templateCollection.id, data);
    // TODO(BNCH-161049): Save the review process fire-and-forget instead of blocking the modal's save
    // flow on the Celery task — close the modal immediately and surface success/failure via toasts.
    await this.saveReviewLifecycleIfChanged();
    return response;
  };

  deleteTemplateCollection = (): JQueryPromise<any> =>
    TemplateCollectionActions.deleteWithXHR(this.props.templateCollection.id);

  getDisabledOwnerHelpContent = (): React.ReactNode => {
    const numRecipes = getNumTemplatesOfType(
      this.props.templateCollection,
      ModelTypeConstants.RECIPE_PROCEDURE
    );
    const numAssays = getNumTemplatesOfType(
      this.props.templateCollection,
      ModelTypeConstants.ASSAY_PROCEDURE
    );

    const procedureLabels: Array<string> = [];
    if (numRecipes > 0) {
      procedureLabels.push(
        getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.RECIPE, {
          stringCase: StringCase.LOWER,
          plural: true,
        })
      );
    }
    if (numAssays > 0) {
      procedureLabels.push(
        getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.ASSAY, {
          stringCase: StringCase.LOWER,
          plural: true,
        })
      );
    }
    const procedureDescription = procedureLabels.join(' and ');

    return (
      <span>
        {`To change the owner, move ${procedureDescription} to a different template collection. `}
        <Link
          href="#"
          className={css(styles.viewProceduresLink)}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            this.props.onRequestClose();
            this.props.onFilterViewToProceduresOnly?.();
          }}
        >
          {`View ${procedureDescription}`}
        </Link>
      </span>
    );
  };

  override render(): JSX.Element | null {
    const owner: Owner = this.props.templateCollection.ownerJson;
    const ownerInfo: OwnerInfo = {
      type: this.props.templateCollection.ownerInfoJson.type,
      memberDataPolicyId: this.props.templateCollection.ownerInfoJson.memberDataPolicyId || null,
    };
    const collaborators: Array<Collaborator> = this.props.templateCollection.collaboratorsJson.filter(
      (collaborator: any) => collaborator.id !== owner.id
    );

    const templateCollectableOwnable: OwnableInfo = {
      id: this.props.templateCollection.id,
      name: this.props.templateCollection.name,
      description: this.props.templateCollection.description,
      owner,
      ownerInfo,
      collaborators,
    };

    const shouldDisableChangeOwner =
      doesTemplateCollectionIncludeProcedures(this.props.templateCollection) && isOrganization(owner);

    return (
      <OwnableSettingsWithCollaborationsModal
        disableChangeOwner={shouldDisableChangeOwner}
        disabledOwnerHelpContent={shouldDisableChangeOwner ? this.getDisabledOwnerHelpContent() : undefined}
        navigateOnSuccess={this.props.navigateOnSuccess}
        onRequestClose={this.props.onRequestClose}
        existingOwnable={templateCollectableOwnable}
        ownableType={ModelTypeConstants.TEMPLATE_COLLECTION}
        ownableId={templateCollectableOwnable.id}
        customSettingsComponent={this.renderReviewProcessSection}
        isReadOnly={!this.props.templateCollection?.permissions?.admin}
        doSubmit={(data: UpdateOwnableParams) =>
          this.updateTemplateCollection({
            ...data,
            collaborators:
              data.collaborations &&
              convertCollaborationsToCollaborators(data.collaborations.filter((c) => !c.fromOwnership)),
          })
        }
        doDelete={
          !BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING && BenchlingConfig.ENABLE_DELETE
            ? this.deleteTemplateCollection
            : null
        }
      />
    );
  }
}

const styles = StyleSheet.create({
  viewProceduresLink: {
    fontSize: 12,
  },
});

export default ManageTemplateCollectionModal;
